/**
 * 閲覧の通知([ADR-0042](../../../../docs/adr/0042-view-count-beacon.md))。
 *
 * **表示が終わったら `POST /pub/v1/views/{token}` を 1 回送る。** 公開 API の
 * 取得は CloudFront の 60 秒キャッシュから返るため、取得の回数では閲覧を
 * 数えられない(ADR-0042 の論点 1)。
 *
 * **表示を妨げない。** 完了を待たず、失敗は捨てる。数え漏れは許容する
 * (「何回開かれたか」の概数 —— ADR-0042 の「引き受ける制約」)。
 */

/** 同じブラウザ・同じリンクを数え直すまでの間隔(同意書 Q2)。 */
export const VIEW_DEDUPE_MS = 30 * 60 * 1000;

/**
 * `localStorage` のキーの接頭辞。
 *
 * **埋め込みでは埋め込み先サイトの保存領域に書く**(埋め込みは外部サイトの
 * ページ上で動く)。他のキーとぶつからないよう名前空間を切り、
 * **書くのはトークンと時刻だけ**にする。
 */
export const VIEW_STORAGE_PREFIX = 'signa:view:';

export interface ViewBeaconDeps {
  readonly now: () => number;
  /**
   * 保存領域。**取り出すだけで投げる環境がある**(Cookie を拒否した Safari の
   * 埋め込み等で `SecurityError`)ため、関数で受けて呼び出しごと try で囲む。
   */
  readonly storage: () => Pick<Storage, 'getItem' | 'setItem'> | null;
  readonly fetch: typeof fetch;
}

const defaultDeps: ViewBeaconDeps = {
  now: () => Date.now(),
  storage: () => (typeof localStorage === 'undefined' ? null : localStorage),
  fetch: (...args) => fetch(...args),
};

export function viewUrl(apiBase: string, token: string): string {
  return `${apiBase}/pub/v1/views/${encodeURIComponent(token)}`;
}

/** 共有フォルダのタブの閲覧の通知先(#290 / [ADR-0051](../../../../docs/adr/0051-folder-view-count.md) の決定 5)。 */
export function folderViewUrl(apiBase: string, folderToken: string, tabId: string): string {
  return `${apiBase}/pub/v1/views/${encodeURIComponent(folderToken)}/${encodeURIComponent(tabId)}`;
}

/**
 * 閲覧を通知する。**送ったら `true`**(テスト用。呼び出し側は使わない)。
 *
 * **送る前に時刻を書く。** 同じページに同じトークンの枠が複数あっても、
 * 2 枠目は書かれた時刻を読んで止まる(書き込みは同期的)。
 *
 * **保存領域が使えなければ毎回送る。** 数え過ぎの側に倒す —— 送らない側に
 * 倒すと、プライベートブラウズの閲覧が**一切**数えられなくなる。
 */
export function sendView(
  apiBase: string,
  token: string,
  deps: ViewBeaconDeps = defaultDeps,
): boolean {
  return beacon(viewUrl(apiBase, token), `${VIEW_STORAGE_PREFIX}${token}`, deps);
}

/**
 * 共有フォルダのタブの閲覧を通知する(#290 / ADR-0051 の決定 7)。**送ったら `true`**。
 *
 * **30 分判定はタブごと**(`signa:view:<フォルダのトークン>:<タブの id>`)—— 同じフォルダの
 * 同じ盤面は 30 分に 1 回。**別のタブ・別のフォルダ・盤面のリンクとは別に数える**
 * (キーが違う —— 盤面のリンクのキーはトークンだけで、`:` を含まない)。送り方は `sendView` と同じ。
 */
export function sendFolderView(
  apiBase: string,
  folderToken: string,
  tabId: string,
  deps: ViewBeaconDeps = defaultDeps,
): boolean {
  return beacon(
    folderViewUrl(apiBase, folderToken, tabId),
    `${VIEW_STORAGE_PREFIX}${folderToken}:${tabId}`,
    deps,
  );
}

/** 30 分判定をして、本文なしの POST を 1 回送る(盤面・共有フォルダで共通)。 */
function beacon(url: string, key: string, deps: ViewBeaconDeps): boolean {
  const now = deps.now();

  try {
    const storage = deps.storage();
    if (storage !== null) {
      const last = Number(storage.getItem(key));
      // 未設定は `Number(null) = 0`。**未来の時刻**(端末の時計を戻した)は数え直す。
      if (last > 0 && last <= now && now - last < VIEW_DEDUPE_MS) return false;
      storage.setItem(key, String(now));
    }
  } catch {
    // 使えなくても送る(上の注記)。
  }

  try {
    /*
     * **`cors` の本文なし POST = 単純リクエスト。** プリフライトは出ない。
     * 応答は読まない(サーバーは常に 204 —— ADR-0042 の決定 3)。
     *
     * **`no-cors` にしない**([ADR-0043](../../../../docs/adr/0043-view-beacon-cors-mode.md))。
     * `no-cors` の POST は、埋め込み先の `Referrer-Policy` が `no-referrer` などだと
     * **`Origin: null` で届き**、許可リストに照らされて**その埋め込みが 1 件も
     * 数えられない。** `cors` なら `Origin` は常に実際の値になる。
     *
     * **`credentials: 'omit'`。** `sendBeacon` を使わないのはこれのため
     * (あちらは Cookie を必ず送る — ADR-0042 の論点 2)。
     *
     * **`keepalive`。** 表示直後にページを閉じられても届くように。
     */
    void deps
      .fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
      })
      .catch(() => {
        // 失敗は捨てる。
      });
  } catch {
    // `fetch` が同期的に投げる環境(古いブラウザで `keepalive` 非対応等)でも表示は続ける。
  }
  return true;
}
